import { PermissionCode, RoleCode, type RoleOption } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { ApiRequestError } from '@/lib/api';
import { useAssignRolesToMany } from '@/platform/features/users/use-users';

const DIRECTORY_ROLES = new Set<string>([
  RoleCode.END_USER,
  RoleCode.IT_AGENT,
  RoleCode.IT_LEAD,
  RoleCode.IT_ADMIN,
]);

type AssignRolesDialogProps = {
  open: boolean;
  userIds: string[];
  roles: RoleOption[];
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
};

export function AssignRolesDialog({
  open,
  userIds,
  roles,
  onOpenChange,
  onAssigned,
}: AssignRolesDialogProps) {
  const assign = useAssignRolesToMany();
  const me = useMe();
  const canAssignOperator = hasPermission(me.data, PermissionCode.TENANTS_MANAGE);
  const visibleRoles = roles.filter(
    (role) => role.code !== RoleCode.PLATFORM_OPERATOR || canAssignOperator,
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [removeOpen, setRemoveOpen] = useState(false);
  const count = userIds.length;
  const canSubmit = selected.length > 0 && count > 0 && !assign.isPending;
  const showsDirectoryNote = selected.some((code) => DIRECTORY_ROLES.has(code));

  function toggle(code: string, checked: boolean) {
    setSelected((current) => {
      if (checked) {
        return current.includes(code) ? current : [...current, code];
      }
      return current.filter((item) => item !== code);
    });
  }

  function closeAll() {
    setRemoveOpen(false);
    setSelected([]);
    onOpenChange(false);
  }

  async function submit(mode: 'add' | 'remove') {
    if (!canSubmit) {
      return;
    }
    try {
      const result = await assign.mutateAsync({ userIds, roles: selected, mode });
      toast.success(
        result.updated > 0
          ? `Berechtigungsgruppen bei ${result.updated} Personen aktualisiert.`
          : 'Keine Änderung.',
      );
      closeAll();
      onAssigned();
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Berechtigungsgruppen konnten nicht zugewiesen werden.',
      );
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setSelected([]);
            setRemoveOpen(false);
          }
          onOpenChange(next);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Berechtigungsgruppen zuweisen</DialogTitle>
            <DialogDescription>
              {count} Personen. Bestehende Gruppen bleiben erhalten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid max-h-80 gap-3 overflow-y-auto sm:grid-cols-2">
            {visibleRoles.map((role) => (
              <label
                key={role.id}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 text-sm shadow-sm"
              >
                <Checkbox
                  checked={selected.includes(role.code)}
                  disabled={assign.isPending}
                  onCheckedChange={(checked) => toggle(role.code, checked === true)}
                />
                <span>
                  <span className="font-medium">{role.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {showsDirectoryNote ? (
            <p className="text-sm text-muted-foreground">
              Diese vier Gruppen kann der nächste Abgleich mit Microsoft 365 wieder setzen. Eigene
              Gruppen bleiben.
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" disabled={!canSubmit} onClick={() => void submit('add')}>
              {assign.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : null}
              Hinzufügen
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!canSubmit}
              onClick={() => setRemoveOpen(true)}
            >
              Entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Berechtigungsgruppen entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die markierten Gruppen werden bei {count} Personen entfernt. Jede Person behält
              mindestens eine Gruppe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assign.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={assign.isPending}
              onClick={(event) => {
                event.preventDefault();
                void submit('remove');
              }}
            >
              {assign.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : null}
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
